import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AxiosError } from "axios";
import { api } from "../api/client";
import { COUNTRY_OPTIONS } from "../lib/countries";
import { OnboardingAssistant } from "../components/OnboardingAssistant";

// Public trial/onboarding application. No login, no payment — the business KYC
// a voice provider needs to vet a new customer before a trial: identity,
// physical address, an authorized contact, the intended use-case/consent, and
// the TCPA/DNC acknowledgements. Posts to the public POST /api/onboarding/.

const ENTITY_TYPES = [
  "LLC", "Corporation", "S-Corporation", "Sole Proprietor",
  "Partnership", "Nonprofit", "Government", "Other",
];
const TRAFFIC_TYPES = [
  "Marketing/Sales", "Transactional/Notifications", "Appointment Reminders",
  "Collections", "Political", "Surveys/Research", "Customer Service", "Other",
];
const VOLUMES = ["Under 1,000", "1,000–10,000", "10,000–100,000", "100,000+"];

const EMPTY = {
  legal_business_name: "", dba_name: "", entity_type: "", tax_id_ein: "",
  incorporation_region: "", website: "",
  address_line1: "", address_line2: "", city: "", state_region: "", postal_code: "", country: "United States",
  contact_name: "", contact_title: "", contact_email: "", contact_phone: "",
  use_case: "", traffic_type: "", expected_daily_volume: "", calling_regions: "", consent_method: "",
  attest_consent: false, attest_dnc: false, attest_accurate: false, agree_terms: false,
  referral_source_id: "", // honeypot (named to avoid autofill)
};
type Form = typeof EMPTY;

export function OnboardingPage() {
  const [f, setF] = useState<Form>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  function set<K extends keyof Form>(k: K, v: Form[K]) {
    setF((prev) => ({ ...prev, [k]: v }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!(f.attest_consent && f.attest_dnc && f.attest_accurate && f.agree_terms)) {
      setError("Please confirm all four acknowledgements at the bottom.");
      return;
    }
    setBusy(true);
    try {
      const resp = await api.post<{ data?: { application_id?: number | null } }>("/onboarding/", f);
      // A null application_id means the server accepted the request but did not
      // store it (the bot honeypot fired). Never show a false "received" — if a
      // real applicant's browser autofilled the hidden field, tell them how to
      // reach us instead of silently dropping the lead.
      if (resp.data?.data?.application_id == null) {
        setError("We couldn't process your application automatically. Please email " +
                 "sales@reliadata.io and we'll get you set up.");
        return;
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 429) {
        setError("Too many applications from your network. Please try again later or email sales@reliadata.io.");
      } else if (err instanceof AxiosError && err.response?.status === 422) {
        // FastAPI validation errors put an ARRAY of {loc,msg,…} in `detail`;
        // our own guard puts a string there. Only render a string — an array
        // would crash React's {error} render ("objects are not valid as a child").
        const d = err.response?.data?.detail;
        setError(typeof d === "string" ? d : "Please review your entries and try again.");
      } else {
        setError("Something went wrong submitting your application. Please try again, or email sales@reliadata.io.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-10 text-center">
          <div className="text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Application received</h1>
          <p className="text-slate-600 mb-6">
            Thanks, {f.contact_name.split(" ")[0] || "there"}. Our team will review your
            application for <span className="font-medium">{f.legal_business_name}</span> and
            reach out at <span className="font-medium">{f.contact_email}</span> to get your trial set up.
          </p>
          <Link to="/" className="inline-block bg-brand hover:bg-brand-dark text-white rounded-lg px-5 py-2.5 text-sm font-medium">
            Back to home
          </Link>
        </div>
        <OnboardingAssistant />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="block text-center text-2xl font-extrabold text-brand mb-1">Reliadata</Link>
        <h1 className="text-center text-3xl font-bold text-slate-900 mb-1">Apply for a trial</h1>
        <p className="text-center text-sm text-slate-500 mb-8 max-w-xl mx-auto">
          Tell us about your business and how you plan to use the platform. As a voice provider
          we&apos;re required to know our customers, so a few compliance questions are included.
          No payment details are collected.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot: visually hidden, off-screen; a real user never fills it. */}
          <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
            <label>Do not fill this in
              <input tabIndex={-1} autoComplete="off" value={f.referral_source_id}
                     onChange={(e) => set("referral_source_id", e.target.value)} />
            </label>
          </div>

          <Section title="Company information">
            <Field label="Legal business name" required>
              <Text value={f.legal_business_name} onChange={(v) => set("legal_business_name", v)} required minLength={2} />
            </Field>
            <Field label="DBA / trade name" hint="if different from legal name">
              <Text value={f.dba_name} onChange={(v) => set("dba_name", v)} />
            </Field>
            <Field label="Business type" required>
              <Select value={f.entity_type} onChange={(v) => set("entity_type", v)} options={ENTITY_TYPES} placeholder="Select…" required />
            </Field>
            <Field label="Tax ID / business registration no." hint="EIN, VAT, company number — whatever applies">
              <Text value={f.tax_id_ein} onChange={(v) => set("tax_id_ein", v)} maxLength={32} />
            </Field>
            <Field label="Region / country of registration">
              <Text value={f.incorporation_region} onChange={(v) => set("incorporation_region", v)} maxLength={128} placeholder="e.g. Delaware, US · England & Wales" />
            </Field>
            <Field label="Website">
              <Text value={f.website} onChange={(v) => set("website", v)} placeholder="https://" />
            </Field>
          </Section>

          <Section title="Business address" hint="physical address — no PO boxes">
            <Field label="Country" required>
              <Select value={f.country} onChange={(v) => set("country", v)} options={COUNTRY_OPTIONS} placeholder="Select…" required />
            </Field>
            <Field label="Street address" required>
              <Text value={f.address_line1} onChange={(v) => set("address_line1", v)} required minLength={2} />
            </Field>
            <Field label="Suite / unit">
              <Text value={f.address_line2} onChange={(v) => set("address_line2", v)} />
            </Field>
            <Field label="City / town" required>
              <Text value={f.city} onChange={(v) => set("city", v)} required maxLength={128} />
            </Field>
            <Field label="State / province / region" required>
              <Text value={f.state_region} onChange={(v) => set("state_region", v)} required maxLength={128} />
            </Field>
            <Field label="Postal / ZIP code" required>
              <Text value={f.postal_code} onChange={(v) => set("postal_code", v)} required minLength={2} maxLength={32} />
            </Field>
          </Section>

          <Section title="Authorized contact" hint="someone who can act for the business">
            <Field label="Full name" required>
              <Text value={f.contact_name} onChange={(v) => set("contact_name", v)} required minLength={2} maxLength={191} />
            </Field>
            <Field label="Title / role" required>
              <Text value={f.contact_title} onChange={(v) => set("contact_title", v)} required maxLength={128} />
            </Field>
            <Field label="Work email" required>
              <Text type="email" value={f.contact_email} onChange={(v) => set("contact_email", v)} required />
            </Field>
            <Field label="Phone" required hint="include country code, e.g. +1 512 555 0100">
              <Text type="tel" value={f.contact_phone} onChange={(v) => set("contact_phone", v)} placeholder="+1 512 555 0100" required minLength={7} maxLength={48} />
            </Field>
          </Section>

          <Section title="How you'll use the platform">
            <Field label="Describe your use case" required wide
                   hint="what you'll be calling about, and who you're calling">
              <Area value={f.use_case} onChange={(v) => set("use_case", v)} required minLength={10} />
            </Field>
            <Field label="Type of traffic" required>
              <Select value={f.traffic_type} onChange={(v) => set("traffic_type", v)} options={TRAFFIC_TYPES} placeholder="Select…" required />
            </Field>
            <Field label="Expected daily volume">
              <Select value={f.expected_daily_volume} onChange={(v) => set("expected_daily_volume", v)} options={VOLUMES} placeholder="Select…" />
            </Field>
            <Field label="Calling regions">
              <Text value={f.calling_regions} onChange={(v) => set("calling_regions", v)} maxLength={128} placeholder="e.g. US & Canada" />
            </Field>
            <Field label="How do you obtain consent to call these numbers?" required wide
                   hint="e.g. web opt-in, existing customer relationship, signed agreement">
              <Area value={f.consent_method} onChange={(v) => set("consent_method", v)} required minLength={10} />
            </Field>
          </Section>

          <Section title="Compliance acknowledgements">
            <div className="sm:col-span-2 space-y-3">
              <Check checked={f.attest_consent} onChange={(v) => set("attest_consent", v)}
                     label="We have the required consent to contact every number we dial, and we comply with the telemarketing and consent laws that apply where we call — including the U.S. TCPA for calls to U.S. numbers." />
              <Check checked={f.attest_dnc} onChange={(v) => set("attest_dnc", v)}
                     label="We honor Do-Not-Call and opt-out requests promptly, wherever we operate." />
              <Check checked={f.attest_accurate} onChange={(v) => set("attest_accurate", v)}
                     label="The information in this application is accurate and I am authorized to submit it on behalf of the business." />
              <Check checked={f.agree_terms} onChange={(v) => set("agree_terms", v)}
                     label="We agree to use the platform lawfully and in line with the Acceptable Use Policy." />
            </div>
          </Section>

          <div className="flex items-center justify-between pt-2">
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">Cancel</Link>
            <button type="submit" disabled={busy}
                    className="bg-brand hover:bg-brand-dark disabled:opacity-60 text-white rounded-lg px-6 py-2.5 text-sm font-medium">
              {busy ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </form>
      </div>
      <OnboardingAssistant />
    </div>
  );
}

/* ---- small presentational helpers, styled to match the rest of the SPA ---- */

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {hint && <p className="text-xs text-slate-400 mb-4">{hint}</p>}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${hint ? "" : "mt-4"}`}>{children}</div>
    </div>
  );
}

function Field({ label, required, hint, wide, children }:
  { label: string; required?: boolean; hint?: string; wide?: boolean; children: React.ReactNode }) {
  // The control is nested INSIDE the <label>, which associates them for screen
  // readers and makes the label clickable — without threading an id through
  // every field.
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="block text-sm text-slate-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1 font-normal">{hint}</p>}
    </label>
  );
}

const INPUT = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand";

function Text({ value, onChange, type = "text", placeholder, maxLength = 255, required, minLength }:
  { value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
    maxLength?: number; required?: boolean; minLength?: number }) {
  return <input type={type} value={value} placeholder={placeholder} maxLength={maxLength}
                required={required} minLength={minLength}
                onChange={(e) => onChange(e.target.value)} className={INPUT} />;
}

function Area({ value, onChange, required, minLength }:
  { value: string; onChange: (v: string) => void; required?: boolean; minLength?: number }) {
  // 4000 matches the backend cap on use_case / consent_method, so a long paste
  // is trimmed in the field rather than bounced with a 422 on submit.
  return <textarea value={value} rows={3} maxLength={4000} required={required} minLength={minLength}
                   onChange={(e) => onChange(e.target.value)} className={INPUT} />;
}

function Select({ value, onChange, options, placeholder, required }:
  { value: string; onChange: (v: string) => void; options: string[]; placeholder?: string; required?: boolean }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} required={required} className={INPUT}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Check({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
             className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" />
      <span>{label}</span>
    </label>
  );
}
